import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Table, Space, Tag, Alert, Empty, Spin, Tooltip, Segmented,
} from 'antd';
import dayjs from 'dayjs';
import api from '../../libs/api';
import ReceiptPace from './ReceiptPace';
import {
  INK, NEUTRAL, OVER, UNDER, BUDGET_INK, FAINT, fmtMoney, fmtPct, FRAME, frameColumns,
} from './shared';

/**
 * The three statements, over the same eight columns as every other tab.
 *
 * Nothing here is modelled a second time. Revenue and cost of sales are what GP
 * Control saved, operating expense is Opex Control's budget, balance sheet
 * movements are what the movements tab saved. If a figure here disagrees with
 * the tab it came from, that is a bug rather than a difference of method — and
 * that is exactly why those tabs save their outputs rather than their inputs.
 *
 * Every top-level line opens. A reader who doubts a number should be able to
 * see what is inside it without going to Xero to find out, and a statement
 * whose totals cannot be taken apart is a statement nobody can argue with —
 * which sounds like a strength and is the opposite.
 *
 * Forecast columns carry account detail only where the forecast itself has it.
 * Opex is budgeted per account and opens; gross profit is modelled in total and
 * does not, because spreading a modelled total across accounts would invent
 * detail nobody entered.
 */

const STATEMENTS = [
  { key: 'pl', label: 'Profit & loss' },
  { key: 'bs', label: 'Balance sheet' },
  { key: 'cash', label: 'Cashflow' },
];

export default function Statements({ entity }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [which, setWhich] = useState('pl');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get('/api/cashflow/statements', { params: { entity }, timeout: 180000 });
      setData(r.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not load the statements');
      setData(null);
    }
    setLoading(false);
  }, [entity]);

  useEffect(() => { load(); }, [load]);

  if (error) return <Alert type="error" showIcon message={error} />;
  if (!data) {
    return (
      <Card size="small">
        <Spin spinning={loading}>
          <Empty description={loading ? 'Reading the ledger…' : 'No data'} />
        </Spin>
      </Card>
    );
  }

  const { months } = data;

  const num = (v, row, kind) => {
    if (v == null) return <span style={{ color: FAINT }}>—</span>;
    if (row.percent) {
      return (
        <span style={{ fontVariantNumeric: 'tabular-nums', color: kind === 'budget' ? BUDGET_INK : INK }}>
          {fmtPct(v)}
        </span>
      );
    }
    if (!v) return <span style={{ color: FAINT }}>—</span>;
    const colour = kind === 'budget' ? BUDGET_INK
      : (row.signed ? (v < 0 ? OVER : UNDER) : INK);
    return (
      <span style={{
        fontVariantNumeric: 'tabular-nums',
        color: colour,
        fontWeight: row.strong ? 600 : 400,
      }}
      >
        {fmtMoney(v)}
      </span>
    );
  };

  /**
   * A statement's rows, with the accounts underneath each one.
   *
   * antd expands on `children`, and a row with an empty `children` array still
   * draws an expander that opens onto nothing — so a line with no detail gets
   * no array at all.
   */
  const withChildren = (row, children) => {
    if (!children || !children.length) return row;
    return {
      ...row,
      children: children
        .slice()
        .sort((a, b) => {
          const sum = (x) => Object.values(x.actual || {}).reduce((t, v) => t + Math.abs(v), 0)
            + Object.values(x.forecast || {}).reduce((t, v) => t + Math.abs(v), 0);
          return sum(b) - sum(a);
        })
        .map((c) => ({
          key: `${row.key}:${c.key}`,
          label: c.name,
          accountCode: c.accountCode,
          child: true,
          // A child answers the same two questions its parent does: what the
          // ledger says, and what the forecast says. Reading one map for both
          // would put an actual under a forecast heading.
          pick: (m, kind) => ((kind === 'budget' ? c.forecast?.[m] : c.actual?.[m]) ?? null),
        })),
    };
  };

  const labelColumn = {
    title: '', dataIndex: 'label', width: 250, fixed: 'left',
    render: (v, r) => (
      <span style={{
        color: r.child ? NEUTRAL : (r.muted ? NEUTRAL : INK),
        fontWeight: r.strong ? 600 : 400,
        fontSize: r.child ? 12 : 13,
      }}
      >
        {v}
        {r.child && r.accountCode && (
          <span style={{ color: FAINT, marginLeft: 6 }}>{r.accountCode}</span>
        )}
        {/* Which tab a forecast line comes from. Nothing on this page is
            modelled here, so being able to see the source is what makes a
            disagreement between two tabs a bug rather than an opinion. */}
        {!r.child && r.source && (
          <Tag style={{ marginLeft: 8, fontSize: 10, lineHeight: '16px', color: NEUTRAL }}>
            {r.source}
          </Tag>
        )}
      </span>
    ),
  };

  const statementColumns = (budgetLabel) => [
    labelColumn,
    ...frameColumns(months, (row, m, kind) => {
      if (!m) return <span style={{ color: FAINT }}>—</span>;
      // A child row only ever knows its own months. A forecast month it has no
      // number for shows a dash, not a zero — the detail is missing there, the
      // amount is not zero.
      const v = row.pick ? row.pick(m, kind) : null;
      return num(v, row, kind);
    }, { budgetLabel }),
  ];

  /**
   * Which map a column reads.
   *
   * The eight-column frame shows this month twice — once as the ledger to date
   * and once as the forecast for the whole month — so a cell cannot be resolved
   * from its month alone. Everything below goes through here.
   */
  const at = (map, forecastMap) => (m, kind) => (kind === 'budget' ? forecastMap?.[m] : map?.[m]) || null;

  // ---------------------------------------------------------------- P&L
  const plAt = at(data.pl, data.plForecast);
  const src = data.plSources || {};
  const plRows = [
    withChildren({
      key: 'revenue', label: 'Revenue', signed: false,
      source: src.revenue,
      pick: (m, kind) => plAt(m, kind)?.revenue ?? null,
    }, data.plChildren?.revenue),
    withChildren({
      key: 'cogs', label: 'Cost of sales',
      source: src.cogs,
      pick: (m, kind) => plAt(m, kind)?.cogs ?? null,
    }, data.plChildren?.cogs),
    {
      key: 'grossProfit', label: 'Gross profit', strong: true, signed: true,
      pick: (m, kind) => plAt(m, kind)?.grossProfit ?? null,
    },
    {
      key: 'gpPct', label: 'GP %', percent: true, muted: true,
      pick: (m, kind) => {
        const x = plAt(m, kind);
        return x?.revenue ? ((x.revenue - x.cogs) / x.revenue) * 100 : null;
      },
    },
    withChildren({
      key: 'opex', label: 'Operating expenses',
      source: src.opex,
      pick: (m, kind) => plAt(m, kind)?.opex ?? null,
    }, data.plChildren?.opex),
    { key: 'otherIncome', label: 'Other income', muted: true, source: src.otherIncome, pick: (m, kind) => plAt(m, kind)?.otherIncome ?? null },
    { key: 'otherExpense', label: 'Other expenses', muted: true, source: src.otherExpense, pick: (m, kind) => plAt(m, kind)?.otherExpense ?? null },
    {
      key: 'netProfit', label: 'Net profit', strong: true, signed: true,
      pick: (m, kind) => plAt(m, kind)?.netProfit ?? null,
    },
  ];

  // ---------------------------------------------------------- balance sheet
  const bsAt = at(data.bs, data.bsForecast);
  const bsLine = (key, label, childKind, opts = {}) => withChildren(
    { key, label, pick: (m, kind) => bsAt(m, kind)?.[key] ?? null, ...opts },
    childKind ? data.bsChildren?.[childKind] : null,
  );
  const bsRows = [
    bsLine('bank', 'Cash at bank', 'bank', { strong: true }),
    bsLine('currentAssets', 'Other current assets', 'currentAssets'),
    bsLine('fixedAssets', 'Fixed assets', 'fixedAssets'),
    { key: 'totalAssets', label: 'Total assets', strong: true, pick: (m, kind) => bsAt(m, kind)?.totalAssets ?? null },
    bsLine('currentLiabilities', 'Current liabilities', 'currentLiabilities'),
    bsLine('termLiabilities', 'Term liabilities', 'termLiabilities'),
    { key: 'totalLiabilities', label: 'Total liabilities', strong: true, pick: (m, kind) => bsAt(m, kind)?.totalLiabilities ?? null },
    { key: 'netAssets', label: 'Net assets', strong: true, signed: true, pick: (m, kind) => bsAt(m, kind)?.netAssets ?? null },
    bsLine('equity', 'Equity', 'equity', { muted: true }),
  ];

  // ---------------------------------------------------------------- cashflow
  const cashAt = at(data.cash, data.cashForecast);
  const cashRows = [
    { key: 'openingBank', label: 'Opening cash', muted: true, pick: (m, kind) => cashAt(m, kind)?.openingBank ?? null },
    withChildren({
      key: 'operating', label: 'Operating', signed: true,
      pick: (m, kind) => cashAt(m, kind)?.operating ?? null,
    }, data.cashChildren?.operating),
    withChildren({
      key: 'investing', label: 'Investing', signed: true,
      pick: (m, kind) => cashAt(m, kind)?.investing ?? null,
    }, data.cashChildren?.investing),
    withChildren({
      key: 'financing', label: 'Financing', signed: true,
      pick: (m, kind) => cashAt(m, kind)?.financing ?? null,
    }, data.cashChildren?.financing),
    { key: 'movement', label: 'Net movement', strong: true, signed: true, pick: (m, kind) => cashAt(m, kind)?.movement ?? null },
    { key: 'closingBank', label: 'Closing cash', strong: true, pick: (m, kind) => cashAt(m, kind)?.closingBank ?? null },
  ];

  /**
   * Whether the derived cashflow ties to the bank movement Xero reports.
   *
   * It should always, by construction. A discrepancy therefore means a balance
   * sheet section is classified differently from what this page assumes — and
   * saying so is more use than a statement that has quietly stopped meaning
   * anything.
   */
  const drift = data.actualMonths.map((m) => {
    const c = data.cash?.[m];
    if (!c || c.observedMovement == null || c.movement == null) return null;
    return Math.abs(c.movement - c.observedMovement) > 1 ? m : null;
  }).filter(Boolean);

  const table = {
    pl: { rows: plRows, empty: 'No profit and loss in this window', budgetLabel: 'forecast' },
    bs: { rows: bsRows, empty: 'No balance sheet in this window', budgetLabel: 'forecast' },
    cash: { rows: cashRows, empty: 'No cash movements in this window', budgetLabel: 'forecast' },
  }[which];

  return (
    <Spin spinning={loading}>
      <Space direction="vertical" size={14} style={{ width: '100%' }}>
        {(data.plError || data.bsError) && (
          <Alert
            type="warning" showIcon
            message={(
              <span style={{ fontSize: 12 }}>
                {data.plError && `Profit and loss unavailable — ${data.plError}. `}
                {data.bsError && `Balance sheet unavailable — ${data.bsError}. `}
                Forecast columns are unaffected; actual columns are empty rather than zero.
              </span>
            )}
          />
        )}

        {drift.length > 0 && (
          <Alert
            type="warning" showIcon
            message={(
              <span style={{ fontSize: 12 }}>
                The cashflow does not tie to the bank movement in{' '}
                {drift.map((m) => dayjs(m).format('MMM')).join(', ')}. A balance sheet section is
                classified differently from what this page assumes, so the operating / investing /
                financing split there is unreliable — the balance sheet itself is not.
              </span>
            )}
          />
        )}

        {which === 'cash' && (
          <ReceiptPace receipts={data.receipts} collectionRatio={data.assumptions.collectionRatio} />
        )}

        <Card
          size="small"
          title={(
            <Space size="small" wrap>
              <span>{data.name}</span>
              <Tag color="default">
                {dayjs(months[0]).format('MMM YY')} – {dayjs(months[months.length - 1]).format('MMM YY')}
              </Tag>
              <Tooltip title="Months up to and including this one come from Xero. Later months are forecast: revenue and cost of sales from GP Control, operating expenses from Opex Control, and the rest from the balance sheet movements tab.">
                <Tag color="gold">{data.forecastMonths.length} forecast columns</Tag>
              </Tooltip>
              <Tag color={which === 'cash' ? 'green' : 'blue'}>
                {which === 'cash' ? 'cash basis' : 'accrual basis'}
              </Tag>
            </Space>
          )}
          extra={(
            <Segmented
              size="small"
              value={which}
              onChange={setWhich}
              options={STATEMENTS.map((x) => ({ label: x.label, value: x.key }))}
            />
          )}
        >
          <Table
            size="small"
            rowKey="key"
            dataSource={table.rows}
            columns={statementColumns(table.budgetLabel)}
            pagination={false}
            scroll={{ x: 250 + FRAME.length * 118 }}
            locale={{ emptyText: table.empty }}
            expandable={{ defaultExpandedRowKeys: [] }}
          />

          <div style={{ fontSize: 11, color: NEUTRAL, marginTop: 10, lineHeight: 1.7 }}>
            Click a line to open the accounts underneath it.{' '}
            {which === 'pl' && (
              <>
                Every forecast line is composed from the tabs beside this one — the tag on each
                line says which — and nothing is modelled here. Revenue and cost of sales are what
                GP Control saved, operating expenses are {data.assumptions.opexSource}, and other
                income and expenses are the B/S Movements lines that reach the P&amp;L. Only the
                expense line has account detail in the forecast columns; gross profit is modelled
                in total.
              </>
            )}
            {which === 'bs' && <>{data.assumptions.balanceSheetRoll}</>}
            {which === 'cash' && (
              <>
                Actual months derive operating, investing and financing from the movement between
                consecutive balance sheets, so the three always add up to the cash that really
                moved. Forecast months use the GP model&apos;s own cash timing where it has one,
                falling back to{' '}
                {data.assumptions.collectionRatio != null
                  ? `the ${Math.round(data.assumptions.collectionRatio * 100)}% of invoiced revenue collected so far`
                  : 'full collection, with no history to go on'}
                , against cost of sales and operating expenses grossed up at{' '}
                {data.assumptions.gstDefaultPct}%, plus whatever the movements tab has saved.
              </>
            )}
          </div>

          {(data.warnings || []).length > 0 && (
            <Alert
              type="info" showIcon style={{ marginTop: 12 }}
              message={(
                <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12 }}>
                  {data.warnings.slice(0, 8).map((w) => <li key={w}>{w}</li>)}
                  {data.warnings.length > 8 && <li>…and {data.warnings.length - 8} more</li>}
                </ul>
              )}
            />
          )}
        </Card>
      </Space>
    </Spin>
  );
}
